/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                                DATABASE SERVICE      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .is('clock_out_time', null)
        .order('clock_in_time', { ascending: false })
        .limit(1)
        .single();                                     ║
 * ║                  const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();omplete React Web App Database Operations                                    ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import { supabase } from './supabase'
import type { User } from '@supabase/supabase-js'

// ===== TYPES & INTERFACES =====

export interface WorkSession {
  id: string
  user_id: string
  clock_in_time: string
  clock_out_time?: string
  total_minutes?: number
  location_lat?: number
  location_lng?: number
  photo_proof_url?: string
  photo_proof_hash?: string
  notes?: string
  platform: string
  app_version: string
  environment: string
  created_at: string
  updated_at: string
}

export interface UserProfile {
  id: string
  user_id: string
  full_name?: string
  email?: string
  company_name?: string
  union_local?: string
  job_title?: string
  hourly_wage?: number
  timezone?: string
  phone_number?: string
  created_at: string
  updated_at: string
}

export interface Company {
  id: number
  name: string
  industry_id?: number
  employee_count?: number
  headquarters_city?: string
  headquarters_state?: string
  is_approved: boolean
  created_at: string
  updated_at: string
}

// ===== PLATFORM METADATA =====

const getPlatformMetadata = () => ({
  platform: 'react-web',
  app_version: '1.0.0-alpha',
  environment: import.meta.env.VITE_APP_ENV || 'development',
  platform_details: {
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timestamp: new Date().toISOString()
  }
})

// ===== DATABASE SERVICE CLASS =====

export class DatabaseService {
  private static instance: DatabaseService
  
  static getInstance(): DatabaseService {
    if (!DatabaseService.instance) {
      DatabaseService.instance = new DatabaseService()
    }
    return DatabaseService.instance
  }

  // ===== WORK SESSION OPERATIONS =====

  async clockIn(userId: string, photoProof?: { url: string; hash: string }): Promise<WorkSession | null> {
    try {
      const metadata = getPlatformMetadata()
      
      const sessionData = {
        user_id: userId,
        clock_in_time: new Date().toISOString(),
        photo_proof_url: photoProof?.url || null,
        photo_proof_hash: photoProof?.hash || null,
        ...metadata
      }

      const { data, error } = await supabase
        .from('time_entries')
        .insert([sessionData])
        .select()
        .single()

      if (error) {
        console.error('❌ Clock in error:', error)
        throw error
      }

      console.log('✅ Clocked in successfully:', data)
      return data
    } catch (error) {
      console.error('❌ Clock in failed:', error)
      return null
    }
  }

  async clockOut(sessionId: string): Promise<WorkSession | null> {
    try {
      const clockOutTime = new Date().toISOString()
      
      // First get the session to calculate total minutes
      const { data: session, error: fetchError } = await supabase
        .from('time_entries')
        .select('*')
        .eq('id', sessionId)
        .single()

      if (fetchError) throw fetchError

      // Calculate total minutes
      const clockInTime = new Date(session.clock_in_time)
      const clockOutTimeDate = new Date(clockOutTime)
      const totalMinutes = Math.floor((clockOutTimeDate.getTime() - clockInTime.getTime()) / (1000 * 60))

      const { data, error } = await supabase
        .from('time_entries')
        .update({
          clock_out_time: clockOutTime,
          total_minutes: totalMinutes,
          updated_at: clockOutTime
        })
        .eq('id', sessionId)
        .select()
        .single()

      if (error) throw error

      console.log('✅ Clocked out successfully:', data)
      return data
    } catch (error) {
      console.error('❌ Clock out failed:', error)
      return null
    }
  }

  async getCurrentWorkSession(userId: string): Promise<WorkSession | null> {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .is('clock_out_time', null)
        .order('clock_in_time', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error
      }

      return data || null
    } catch (error) {
      console.error('❌ Get current session failed:', error)
      return null
    }
  }

  async getWorkSessions(userId: string, limit: number = 50): Promise<WorkSession[]> {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .order('clock_in_time', { ascending: false })
        .limit(limit)

      if (error) throw error

      return data || []
    } catch (error) {
      console.error('❌ Get work sessions failed:', error)
      return []
    }
  }

  // ===== USER PROFILE OPERATIONS =====

  async createUserProfile(user: User): Promise<UserProfile | null> {
    try {
      const profileData = {
        user_id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || user.user_metadata?.name || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .insert([profileData])
        .select()
        .single()

      if (error) throw error

      console.log('✅ User profile created:', data)
      return data
    } catch (error) {
      console.error('❌ Create user profile failed:', error)
      return null
    }
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      return data || null
    } catch (error) {
      console.error('❌ Get user profile failed:', error)
      return null
    }
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single()

      if (error) throw error

      console.log('✅ User profile updated:', data)
      return data
    } catch (error) {
      console.error('❌ Update user profile failed:', error)
      return null
    }
  }

  // ===== COMPANY OPERATIONS =====

  async getApprovedCompanies(): Promise<Company[]> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('is_approved', true)
        .order('name', { ascending: true })

      if (error) throw error

      return data || []
    } catch (error) {
      console.error('❌ Get approved companies failed:', error)
      return []
    }
  }

  async getAllCompanies(): Promise<Company[]> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          *,
          industries!companies_industry_id_fkey (
            id,
            name,
            description
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      return data || []
    } catch (error) {
      console.error('❌ Get all companies failed:', error)
      return []
    }
  }

  async bulkApproveCompanies(companyIds: number[]): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_approved: true,
          updated_at: new Date().toISOString()
        })
        .in('id', companyIds)

      if (error) throw error

      console.log(`✅ Bulk approved ${companyIds.length} companies`)
      return true
    } catch (error) {
      console.error('❌ Bulk approve failed:', error)
      return false
    }
  }

  async bulkRejectCompanies(companyIds: number[]): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .in('id', companyIds)

      if (error) throw error

      console.log(`✅ Bulk rejected ${companyIds.length} companies`)
      return true
    } catch (error) {
      console.error('❌ Bulk reject failed:', error)
      return false
    }
  }

  // ===== ANALYTICS & REPORTING =====

  async getUserStats(userId: string): Promise<{
    totalHours: number
    totalSessions: number
    averageSessionLength: number
    thisWeekHours: number
  }> {
    try {
      const oneWeekAgo = new Date()
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

      const { data: allSessions, error: allError } = await supabase
        .from('time_entries')
        .select('total_minutes, clock_in_time')
        .eq('user_id', userId)
        .not('total_minutes', 'is', null)

      if (allError) throw allError

      const { data: weekSessions, error: weekError } = await supabase
        .from('time_entries')
        .select('total_minutes')
        .eq('user_id', userId)
        .gte('clock_in_time', oneWeekAgo.toISOString())
        .not('total_minutes', 'is', null)

      if (weekError) throw weekError

      const totalMinutes = allSessions?.reduce((sum, session) => sum + (session.total_minutes || 0), 0) || 0
      const thisWeekMinutes = weekSessions?.reduce((sum, session) => sum + (session.total_minutes || 0), 0) || 0
      const totalSessions = allSessions?.length || 0

      return {
        totalHours: Math.round(totalMinutes / 60 * 100) / 100,
        totalSessions,
        averageSessionLength: totalSessions > 0 ? Math.round(totalMinutes / totalSessions * 100) / 100 : 0,
        thisWeekHours: Math.round(thisWeekMinutes / 60 * 100) / 100
      }
    } catch (error) {
      console.error('❌ Get user stats failed:', error)
      return {
        totalHours: 0,
        totalSessions: 0,
        averageSessionLength: 0,
        thisWeekHours: 0
      }
    }
  }

  // ===== HEALTH CHECK =====

  async healthCheck(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id')
        .limit(1)

      return !error
    } catch (error) {
      console.error('❌ Database health check failed:', error)
      return false
    }
  }
}
